/**
 * submitFormToSupabase.ts
 * Submits form entries to Supabase via the submit-form edge function.
 * This makes form submissions visible in the admin panel from ANY browser worldwide.
 */

import { EDGE_FUNCTIONS, getEdgeFunctionHeaders } from "@/utils/edgeFunctionUrl";

const SUBMIT_FORM_EDGE_FN = EDGE_FUNCTIONS.submitForm;

export interface FormField {
  label: string;
  value: string;
}

/**
 * Submit a form entry to Supabase.
 * Fire-and-forget safe — won't throw, just logs on failure.
 */
export async function submitFormToSupabase(
  formId: string,
  formLabel: string,
  fields: FormField[]
): Promise<boolean> {
  try {
    const res = await fetch(SUBMIT_FORM_EDGE_FN, {
      method: "POST",
      headers: getEdgeFunctionHeaders(),
      body: JSON.stringify({ formId, formLabel, fields }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      console.error(`[submitFormToSupabase] Edge function returned HTTP ${res.status}: ${text}`);
    }
    return res.ok;
  } catch (err) {
    console.error("[submitFormToSupabase] Network error:", err);
    return false;
  }
}

/**
 * Fetch all form entries from Supabase (admin panel use).
 */
export interface SupabaseFormEntry {
  id: string;
  form_id: string;
  form_label: string;
  submitted_at: string;
  fields: FormField[];
}

export async function fetchFormEntries(formId?: string): Promise<SupabaseFormEntry[]> {
  try {
    const url = formId
      ? `${SUBMIT_FORM_EDGE_FN}?formId=${encodeURIComponent(formId)}`
      : SUBMIT_FORM_EDGE_FN;
    const res = await fetch(url, {
      method: "GET",
      headers: getEdgeFunctionHeaders(),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      console.error(`[fetchFormEntries] Edge function returned HTTP ${res.status}: ${text}`);
      return [];
    }
    const data = await res.json() as { entries: SupabaseFormEntry[] };
    return data.entries ?? [];
  } catch (err) {
    console.error("[fetchFormEntries] Network error:", err);
    return [];
  }
}

/**
 * Delete a form entry from Supabase (admin panel use).
 */
export async function deleteFormEntryFromSupabase(id: string): Promise<boolean> {
  try {
    const res = await fetch(`${SUBMIT_FORM_EDGE_FN}?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: getEdgeFunctionHeaders(),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      console.error(`[deleteFormEntryFromSupabase] Edge function returned HTTP ${res.status}: ${text}`);
    }
    return res.ok;
  } catch (err) {
    console.error("[deleteFormEntryFromSupabase] Network error:", err);
    return false;
  }
}